// Cronograma de Coleta/Devolução (domínio Operacional): projeta as Paradas de
// cada Cliente numa semana típica — cada Parada gera, nos seus dias e horário,
// um evento de Coleta (ação "coletar" ou "ambos") e/ou um de Devolução (ação
// "devolver" ou "ambos").
//
// Não é atrelado a nenhuma semana real: os dias da semana das Paradas são fixos,
// então a grade também é — daí dar para desenhar como uma grade fixa, sem
// navegar entre semanas.

import type { Snapshot } from "./snapshot.ts";

export type TipoEventoCronograma = "coleta" | "devolucao";

export interface EventoCronograma {
  clienteId: string;
  clienteNome: string;
  paradaId: string;
  /** 0 = domingo … 6 = sábado — mesma convenção de `Parada.diasSemana`. */
  dia: number;
  /** "HH:MM". */
  hora: string;
  tipo: TipoEventoCronograma;
}

/**
 * Projeta as Paradas dos Clientes ativos numa semana típica: para cada Parada,
 * nos seus dias da semana e no seu horário, um evento de Coleta (ação "coletar"
 * ou "ambos") e/ou um de Devolução (ação "devolver" ou "ambos").
 */
export function eventosCronograma(
  snapshot: Pick<Snapshot, "clientes" | "paradas">,
): EventoCronograma[] {
  const nomeClienteAtivo = new Map(
    snapshot.clientes.filter((c) => c.ativo).map((c) => [c.id, c.nome]),
  );
  const eventos: EventoCronograma[] = [];
  for (const p of snapshot.paradas) {
    const clienteNome = nomeClienteAtivo.get(p.clienteId);
    if (!clienteNome) continue;
    for (const dia of p.diasSemana) {
      const base = {
        clienteId: p.clienteId,
        clienteNome,
        paradaId: p.id,
        dia,
        hora: p.horario,
      };
      if (p.acao === "coletar" || p.acao === "ambos") {
        eventos.push({ ...base, tipo: "coleta" });
      }
      if (p.acao === "devolver" || p.acao === "ambos") {
        eventos.push({ ...base, tipo: "devolucao" });
      }
    }
  }
  return eventos;
}

// -------------------------------------------------------------------- grade --

/** Rótulo de cada dia — índice na convenção do domínio (0 = domingo). */
export const DIA_SEMANA_LABEL = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
];

/** As colunas do Cronograma, na ordem de exibição: segunda a domingo. */
export const DIAS_DISPLAY = [1, 2, 3, 4, 5, 6, 0];

/** "Hotel Central Plaza (Coleta)" — o mesmo texto na tela e nas exportações. */
export function rotuloEventoCronograma(e: EventoCronograma): string {
  return `${e.clienteNome} (${e.tipo === "coleta" ? "Coleta" : "Devolução"})`;
}

/** Horários da grade, de 30 em 30 minutos, do primeiro ao último horário entre todos os eventos. */
export function horariosCronograma(horaInicial: number, horaFinal: number): string[] {
  const horas: string[] = [];
  for (let h = horaInicial; h <= horaFinal; h++) {
    horas.push(`${String(h).padStart(2, "0")}:00`);
    horas.push(`${String(h).padStart(2, "0")}:30`);
  }
  return horas;
}

/** O horário arredondado para baixo, para o slot de 30 min a que pertence — `null` se cair fora do intervalo. */
function slotDoHorario(hora: string, horaInicial: number, horaFinal: number): string | null {
  const [h, m] = hora.split(":").map(Number);
  if (h == null || m == null) return null;
  if (h < horaInicial || h > horaFinal) return null;
  return `${String(h).padStart(2, "0")}:${m >= 30 ? "30" : "00"}`;
}

export interface CelulaCronograma {
  dia: number;
  eventos: EventoCronograma[];
}

export interface LinhaCronograma {
  hora: string;
  celulas: CelulaCronograma[];
}

export interface GradeCronograma {
  /** Uma linha por horário, colunas em `DIAS_DISPLAY`. */
  linhas: LinhaCronograma[];
  /** Eventos com horário fora do intervalo dinâmico — não cabem em nenhuma célula. */
  foraDoHorario: EventoCronograma[];
  /** Primeiro horário da grade (ex.: 6). */
  horaInicial: number;
  /** Último horário da grade (ex.: 17). */
  horaFinal: number;
}

/**
 * Calcula o intervalo dinâmico de horários a partir de TODOS os eventos
 * (independente de filtros), arredondando para slots de 30 min.
 */
export function calcularIntervaloHorarios(
  eventos: readonly EventoCronograma[],
): { horaInicial: number; horaFinal: number } {
  if (eventos.length === 0) return { horaInicial: 6, horaFinal: 17 };
  let minH = 24;
  let maxH = 0;
  for (const e of eventos) {
    const [h, m] = e.hora.split(":").map(Number);
    if (h == null) continue;
    const slotH = m != null && m >= 30 ? h : h;
    if (slotH < minH) minH = slotH;
    // Para o final, considerar o slot cheio (00 ou 30)
    if (h > maxH || (h === maxH && m != null && m > 0)) maxH = h;
  }
  return {
    horaInicial: Math.max(0, minH),
    horaFinal: Math.min(23, maxH),
  };
}

/** Organiza os eventos na grade da tela/exportação — um lugar só para os dois. */
export function gradeCronograma(
  eventos: readonly EventoCronograma[],
  horaInicial: number,
  horaFinal: number,
): GradeCronograma {
  const porSlot = new Map<string, EventoCronograma[]>();
  const foraDoHorario: EventoCronograma[] = [];
  for (const e of eventos) {
    const slot = slotDoHorario(e.hora, horaInicial, horaFinal);
    if (slot == null) {
      foraDoHorario.push(e);
      continue;
    }
    const chave = `${e.dia}|${slot}`;
    if (!porSlot.has(chave)) porSlot.set(chave, []);
    porSlot.get(chave)!.push(e);
  }
  const linhas = horariosCronograma(horaInicial, horaFinal)
    .map((hora) => ({
      hora,
      celulas: DIAS_DISPLAY.map((dia) => ({
        dia,
        eventos: ordenarNaCelula(porSlot.get(`${dia}|${hora}`) ?? []),
      })),
    }))
    .filter((linha) => {
      // Horários quebrados (:30) vazios são ocultados para economizar espaço
      if (!linha.hora.endsWith(":30")) return true;
      return linha.celulas.some((c) => c.eventos.length > 0);
    });
  return { linhas, foraDoHorario, horaInicial, horaFinal };
}

/**
 * Ordem dentro de uma célula (mesmo dia e horário): **agrupa por Cliente** e,
 * dentro de cada Cliente, **Devolução antes de Coleta** — no mesmo horário,
 * entrega a roupa limpa antes de pegar a suja. Ex.: com dois hotéis, sai
 * "Hotel A (Devolução), Hotel A (Coleta), Hotel B (Devolução), Hotel B
 * (Coleta)". A Parada desempata para a ordem ser estável.
 */
function ordenarNaCelula(
  eventos: readonly EventoCronograma[],
): EventoCronograma[] {
  return [...eventos].sort(
    (a, b) =>
      a.clienteNome.localeCompare(b.clienteNome, "pt-BR") ||
      (a.tipo === "devolucao" ? 0 : 1) - (b.tipo === "devolucao" ? 0 : 1) ||
      a.paradaId.localeCompare(b.paradaId),
  );
}
